import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Heart, Sparkles } from "lucide-react";

/**
 * SignupPromptModal — inline, on-demand signup gate.
 *
 * Different from `SignupNudgeModal` (which fires on a timer). This one is
 * triggered when an anonymous camper taps an action that requires an
 * account — Save, Report price, Add trip dates, etc. Instead of a dead
 * "Log in to favourite" toast (which puts zero forward motion in the
 * user's hands), it explains the value and links to /register with
 * `?next=` set so signup bounces them straight back to the site they
 * were on.
 *
 * Kept intentionally tiny — one button (create account) + a subtle
 * "sign in instead" fallback. Anything more crowds the conversion CTA.
 */
export default function SignupPromptModal({
  open,
  onClose,
  title = "Save this campsite — free account, 10 seconds",
  body = "Signed-in campers save unlimited favourites, get weather alerts on their trips, and chat with the community.",
  nextPath, // where to send the user after they sign up
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    // lock body scroll while modal open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const registerHref = nextPath
    ? `/register?next=${encodeURIComponent(nextPath)}`
    : "/register";
  const loginHref = nextPath
    ? `/login?next=${encodeURIComponent(nextPath)}`
    : "/login";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-prompt-title"
      onClick={onClose}
      data-testid="signup-prompt-modal"
    >
      <div
        className="relative bg-white w-full max-w-md sm:max-w-lg border-2 border-[#0e0e0e] shadow-[0_8px_0_0_#0e0e0e] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 z-10 text-[#666] hover:text-[#111] p-1.5"
          data-testid="signup-prompt-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="bg-gradient-to-br from-[#ff4500] via-[#ff6a2b] to-[#ffa66c] text-white px-5 pt-5 pb-6 sm:px-7 sm:pt-6 sm:pb-8">
          <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2 py-0.5 text-[10px] mono uppercase tracking-widest font-black mb-3">
            <Sparkles className="w-3 h-3" /> Free · no card · 10 seconds
          </div>
          <h2
            id="signup-prompt-title"
            className="font-display text-2xl sm:text-3xl leading-tight mb-2"
            data-testid="signup-prompt-title"
          >
            {title}
          </h2>
          <p className="text-white/95 text-sm sm:text-base leading-snug">{body}</p>
        </div>

        <div className="px-5 sm:px-7 py-4 sm:py-5 flex flex-col gap-2">
          {/* iter177 — one-tap Google auth right in the prompt. Skips the
              trip through /register entirely; on return, `AuthCallback`
              redirects to /browse (currently) so we tuck the current
              nextPath into localStorage first for the callback to honour. */}
          <button
            type="button"
            onClick={() => {
              try {
                if (nextPath) window.localStorage.setItem("ccu:auth-next", nextPath);
              } catch { /* ignore */ }
              const redirectUrl = window.location.origin + (nextPath || "/browse");
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-[#0e0e0e] hover:bg-[#fafafa] text-[#0e0e0e] font-bold text-sm px-4 py-3 shadow-[0_4px_0_0_#0e0e0e] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#0e0e0e] transition-transform"
            data-testid="signup-prompt-google"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google — 1 tap
          </button>

          <div className="flex items-center gap-3 my-1 text-[10px] mono uppercase tracking-wider text-[#888]">
            <span className="flex-1 h-px bg-[#eee]" /> or email <span className="flex-1 h-px bg-[#eee]" />
          </div>

          <Link
            to={registerHref}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0a7f3f] text-white font-black text-sm uppercase tracking-widest px-4 py-3 shadow-[0_4px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#111] transition-transform"
            data-testid="signup-prompt-cta"
          >
            <Heart className="w-4 h-4" /> Create free account
          </Link>
          <Link
            to={loginHref}
            className="text-[11px] text-[#666] hover:text-[#111] uppercase tracking-widest font-semibold py-1 text-center"
            data-testid="signup-prompt-login"
          >
            Already a member? Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
